import axios from 'axios';
import type { 
  TenantResponse, 
  CreateTenantRequest, 
  UpdateTenantRequest,
  HealthResponse,
  PaginatedLogsResponse,
  LogsQueryParams,
  TenantTaxRateResponse,
  UpdateTaxRateRequest,
} from '@klever-webhook/shared';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
});

// Add auth token to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Health
  async getHealth(): Promise<HealthResponse> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Configs
  async getConfigs(): Promise<TenantResponse[]> {
    const response = await apiClient.get('/configs');
    return response.data;
  },

  async createConfig(data: CreateTenantRequest): Promise<TenantResponse> {
    const response = await apiClient.post('/configs', data);
    return response.data;
  },

  async updateConfig(id: string, data: UpdateTenantRequest): Promise<TenantResponse> {
    const response = await apiClient.put(`/configs/${id}`, data);
    return response.data;
  },

  async deleteConfig(id: string): Promise<void> {
    await apiClient.delete(`/configs/${id}`);
  },

  // Logs
  async getLogs(params: LogsQueryParams): Promise<PaginatedLogsResponse> {
    const response = await apiClient.get('/logs', { params });
    return response.data;
  },

  async retryLog(logId: string): Promise<void> {
    await apiClient.post(`/logs/${logId}/retry`);
  },

  async previewLog(logId: string): Promise<{ metadata: Record<string, unknown> }> {
    const response = await apiClient.get(`/logs/${logId}/preview`);
    return response.data;
  },

  async retrySkippedLogs(tenantId: string): Promise<{ total: number; succeeded: number; failed: number }> {
    const response = await apiClient.post(`/configs/${tenantId}/retry-skipped`);
    return response.data;
  },

  async generateFalcoToken(tenantId: string): Promise<{ token: string }> {
    const response = await apiClient.post(`/configs/${tenantId}/falco-token`);
    return response.data;
  },

  // Tax Rates
  async getTaxRates(tenantId: string): Promise<TenantTaxRateResponse[]> {
    const response = await apiClient.get(`/configs/${tenantId}/tax-rates`);
    return response.data;
  },

  async updateTaxRate(tenantId: string, taxRateId: string, data: UpdateTaxRateRequest): Promise<TenantTaxRateResponse> {
    const response = await apiClient.put(`/configs/${tenantId}/tax-rates/${taxRateId}`, data);
    return response.data;
  },

  async refreshTaxRates(tenantId: string): Promise<{ imported: number; taxRates: TenantTaxRateResponse[] }> {
    const response = await apiClient.post(`/configs/${tenantId}/tax-rates/refresh`);
    return response.data;
  },
};
